import type { Key } from 'react'

import _ from 'lodash'
import { queryCache, useMutation, useQuery } from 'react-query'

import { APPLICATION_QUERY_KEYS } from 'manager/hooks/api/applicationQueries'
import {
  DEAL_MANAGER_QUERY_KEYS,
  GET_DEAL_KEY,
} from 'manager/hooks/api/dealQueries'
import { GET_INCOME_BY_STREAMS } from 'renter/hooks/api'
import { QUERY_KEYS as APPLICATION_RENTER_QUERY_KEYS } from 'renter/hooks/api/applicationQueries'
import { NOTIFICATIONS } from 'shared/config/constants'
import { getSentilinkVerifyReviewedItemLabel } from 'shared/config/sentilinkVerifyReviewedItems'
import {
  EditIncomeAndAssetsParams,
  ApplicationError,
} from 'shared/interfaces/api'
import type {
  Document,
  DocumentInApplication,
} from 'shared/interfaces/api/document'
import api from 'shared/lib/api'
import { isManagerApp } from 'shared/utils/auth'
import { openNotification } from 'shared/utils/ui'

const GET_APPLICANT_EMERGENCY_CONTACTS = 'getApplicantEmergencyContacts'
const GET_APPLICANT_RESIDENTIAL_HISTORY = 'getApplicantResidentialHistory'
const GET_APPLICANT_EMPLOYMENT_HISTORY = 'getApplicantEmploymentHistory'
const GET_APPLICANT_REFERENCES = 'getApplicantReferences'
const LIST_APPLICANT_WORKFLOWS = 'listApplicantWorkflows'
const GET_APPLICANT_WORKFLOW_RUN = 'getApplicantWorkflowRun'
const GET_APPLICATION_FROM_APPLICATION_SERVICE =
  'getApplicationFromApplicationService'
const LIST_APPLICANTS_FROM_APPLICATION_SERVICE =
  'listApplicantsFromApplicationService'
const GET_APPLICANT_FROM_APPLICATION_SERVICE =
  'getApplicantFromApplicationService'
const LIST_APPLICANT_PAYMENTS = 'listApplicantPayments'
const UPSERT_APPLICANT_PAYMENT = 'upsertApplicantPayment'
const GET_OCR_RESULTS = 'getOcrResults'
const GET_OCR_RESULT_URL_BY_ID = 'getOcrResultUrlById'
const GET_OCR_RESULT_BY_ID = 'getOcrResultById'
const GET_OCR_JOBS = 'getOcrJobs'
const GET_DOCUMENTS = 'getDocuments'
export const GET_DOCUMENT_DETAIL = 'getDocumentDetail'

// NOTE: Only an authorized user (manager) can fetch the full SSN.
// So, besides the special permission, as an additional security layer
// we check the user's password along with the application id to confirm
// they have the required privileges to access such information.
export const useViewFullSSN = () => {
  const [viewFullSSN, { isLoading: isLoadingSSN }] = useMutation(
    api.viewFullSSN,
  )

  return {
    viewFullSSN,
    isLoadingSSN,
  }
}

export const useRepairAgreementAttachments = () => {
  const [repairAgreementAttachments, rest] = useMutation(
    api.repairAgreementAttachments,
  )

  return {
    repairAgreementAttachments,
    ...rest,
  }
}

export function useGetApplicantEmergencyContacts() {
  const { data: emergencyContacts, isLoading: isEmergencyContactsLoading } =
    useQuery(
      [GET_APPLICANT_EMERGENCY_CONTACTS],
      () => api.getApplicantEmergencyContacts(),
      {
        onError: () => {
          openNotification(
            'Failed to load emergency contacts',
            NOTIFICATIONS.error,
          )
        },
      },
    )

  return {
    emergencyContacts,
    isEmergencyContactsLoading,
  }
}

export function useGetApplicantResidentialHistory() {
  const { data: residentialHistory, isLoading: isResidentialHistoryLoading } =
    useQuery(
      [GET_APPLICANT_RESIDENTIAL_HISTORY],
      () => api.getApplicantResidentialHistory(),
      {
        onError: () => {
          openNotification(
            'Failed to load residential history',
            NOTIFICATIONS.error,
          )
        },
      },
    )

  return {
    residentialHistory,
    isResidentialHistoryLoading,
  }
}

export function useGetApplicantEmploymentHistory() {
  const { data: employmentHistory, isLoading: isEmploymentHistoryLoading } =
    useQuery(
      [GET_APPLICANT_EMPLOYMENT_HISTORY],
      () => api.getApplicantEmploymentHistory(),
      {
        onError: () => {
          openNotification(
            'Failed to load employment history',
            NOTIFICATIONS.error,
          )
        },
      },
    )

  return {
    employmentHistory,
    isEmploymentHistoryLoading,
  }
}

export function useGetApplicantReferences() {
  const { data: references, isLoading: isReferencesLoading } = useQuery(
    [GET_APPLICANT_REFERENCES],
    () => api.getApplicantReferences(),
    {
      onError: () => {
        openNotification('Failed to load references', NOTIFICATIONS.error)
      },
    },
  )

  return {
    references,
    isReferencesLoading,
  }
}

export const useWithdrawApplication = (applicationId: number) => {
  const [withdrawApplication, { isLoading: isWithdrawApplication }] =
    useMutation(() => api.withdrawApplication({ applicationId }), {
      onSuccess: () => {
        openNotification(
          'You have successfully withdrawn application.',
          NOTIFICATIONS.info,
        )
        queryCache.invalidateQueries(
          APPLICATION_RENTER_QUERY_KEYS.getApplicationInvitation(applicationId),
        )
      },
      onError: (error: ApplicationError) =>
        openNotification(
          error.errors?.error || 'Failed to withdraw application.',
          NOTIFICATIONS.error,
        ),
    })

  return {
    withdrawApplication,
    isWithdrawApplication,
  }
}

export const useEditIncomeAndAssets = (applicationId: Key, dealId?: Key) => {
  const [editIncomeAndAssets, { isLoading: isLoadingEditIncomeAndAssets }] =
    useMutation(
      ({ income, assets }: EditIncomeAndAssetsParams) =>
        api.editIncomeAndAssets({ applicationId, income, assets }),
      {
        onSuccess: () => {
          openNotification(
            'You have successfully edited the applicant\'s total income and balances.',
            NOTIFICATIONS.info,
          )
          queryCache.invalidateQueries(
            APPLICATION_RENTER_QUERY_KEYS.getApplicationInvitation(
              applicationId,
            ),
          )
          // Refresh only the active deal when known; otherwise fall back to the
          // coarse key to preserve previous behavior.
          if (dealId !== undefined && dealId !== null) {
            queryCache.invalidateQueries(DEAL_MANAGER_QUERY_KEYS.getDeal(dealId))
          } else {
            queryCache.invalidateQueries(GET_DEAL_KEY)
          }
        },
        onError: (error: ApplicationError) =>
          openNotification(
            error.errors?.error || 'Failed to edit income and balances.',
            NOTIFICATIONS.error,
          ),
      },
    )

  return {
    editIncomeAndAssets,
    isLoadingEditIncomeAndAssets,
  }
}

export function useGetOcrResults(
  person: string,
  enable: boolean,
  includeRemoved?: boolean,
) {
  const {
    data: ocrResults,
    isLoading: isOcrResultsLoading,
    refetch,
  } = useQuery(
    [GET_OCR_RESULTS, { person }],
    () => api.getOcrResults(person, includeRemoved),
    {
      enabled: enable,
      onError: () => {
        openNotification('Failed to get ocr results', NOTIFICATIONS.error)
      },
    },
  )

  return {
    ocrResults: ocrResults?.data,
    isOcrResultsLoading,
    refetchOcrResults: refetch,
  }
}

export function prefetchOcrResultById(id: string) {
  if (!id) return
  queryCache.prefetchQuery(
    [GET_OCR_RESULT_BY_ID, { id }],
    () => api.getOcrResultById(id),
  )
}

export function useGetOcrResultById(id: string) {
  const { data: ocrResults, isLoading, isFetching } = useQuery(
    [GET_OCR_RESULT_BY_ID, { id }],
    () => api.getOcrResultById(id),
    {
      enabled: !!id,
      onError: () => {
        openNotification('Failed to get ocr results', NOTIFICATIONS.error)
      },
    },
  )

  return {
    ocrResult: ocrResults?.data,
    isLoading: !!id && isLoading,
    isFetching: !!id && isFetching,
  }
}

export function useGetOcrResultUrlById(id: string) {
  const { data: ocrResults } = useQuery(
    [GET_OCR_RESULT_URL_BY_ID, { id }],
    () => api.getOcrResultUrlById(id),
    {
      onError: () => {
        openNotification('Failed to get ocr results', NOTIFICATIONS.error)
      },
    },
  )

  return {
    ocrResult: ocrResults?.data,
  }
}

export const usePatchOcrResultById = () => {
  const [mutation] = useMutation(api.patchOcrResultById, {
    onSuccess: async (_, { id, data }) => {
      const notRevertPatch = Object.values(data).some(Boolean)
      await queryCache.invalidateQueries([GET_OCR_RESULT_BY_ID, { id }])
      // Income-by-streams data is keyed as [GET_INCOME_BY_STREAMS, allowedProviders],
      // so a bare string key for setQueryData misses it (returning undefined and
      // crashing the .map below). Just invalidate — the refetch picks up the new
      // OCR values.
      if (notRevertPatch) {
        await queryCache.invalidateQueries(GET_INCOME_BY_STREAMS)
      } else {
        setTimeout(async () => {
          await queryCache.invalidateQueries(GET_INCOME_BY_STREAMS)
        }, 1500)
      }
      openNotification(
        'Successfully updated the OCR result',
        NOTIFICATIONS.info,
      )
    },
    onError: () =>
      openNotification('Failed updating the OCR result', NOTIFICATIONS.error),
  })

  return {
    patchOcrResultById: mutation,
  }
}

export const useDeleteOcrResultById = () => {
  const [mutation] = useMutation(api.deleteOcrResults, {
    onSuccess: () =>
      openNotification(
        "Successfully removed the document upload result from the applicant's income",
        NOTIFICATIONS.info,
      ),
    onError: () =>
      openNotification(
        "Failed to remove the document upload result from the applicant's income",
        NOTIFICATIONS.error,
      ),
  })

  return {
    deleteOcrResultById: mutation,
  }
}

export function useGetApplicationFromApplicationService(id) {
  const { data: application, isLoading } = useQuery(
    [GET_APPLICATION_FROM_APPLICATION_SERVICE, { id }],
    () => api.getApplicationFromApplicationService(id),
    {
      enabled: !!id,
    },
  )

  return {
    application,
    isLoading,
  }
}

export function useListApplicantsFromApplicationService(application) {
  const { data: applicants, isLoading, refetch } = useQuery(
    [LIST_APPLICANTS_FROM_APPLICATION_SERVICE, { application }],
    () => api.listApplicantsFromApplicationService(application),
    {
      enabled: !!application,
    },
  )

  return {
    applicants,
    isLoading,
    refetch
  }
}

export function useGetApplicantFromApplicationService(id, enabled = true) {
  const { data: applicant, isLoading } = useQuery(
    [GET_APPLICANT_FROM_APPLICATION_SERVICE, { id }],
    () => api.getApplicantFromApplicationService(id),
    {
      enabled: Boolean(id) && enabled,
    },
  )

  return {
    applicant,
    isLoading,
  }
}

export function useListApplicantWorkflows(
  location: string,
  detailed: boolean = false,
) {
  const { data: workflows, isLoading } = useQuery(
    [LIST_APPLICANT_WORKFLOWS, _.pickBy({ location, detailed })],
    () => api.listApplicantWorkflows(location, detailed),
    {
      enabled: !!location,
    },
  )

  return {
    workflows,
    isLoading,
  }
}

export function useGetApplicantWorkflowRun(id) {
  const {
    data: applicantWorkflow,
    isLoading,
    refetch,
  } = useQuery(
    [GET_APPLICANT_WORKFLOW_RUN, { id }],
    () => api.getApplicantWorkflowRun(id),
    {
      enabled: !!id,
    },
  )

  return {
    applicantWorkflow,
    isLoading,
    refetch,
  }
}

export function isWorkflowStarted(applicantWorkflow) {
  const errorMessage =
    applicantWorkflow?.error?.message ||
    applicantWorkflow?.error?.errorMessage ||
    'An error occurred while getting the workflow'
  return !/^((person.+not found)|not found)$/i.test(errorMessage)
}

export function useApplicantWorkflowMutations() {
  const handlers = (s) => ({
    onSuccess: async (data) => {
      await queryCache.invalidateQueries('listApplicantWorkflows')
      await queryCache.invalidateQueries('getApplicantWorkflowRun')

      openNotification(
        _.chain([s, data?.id]).compact().join(' ').value(),
        NOTIFICATIONS.info,
      )
    },
    onError: (error) =>
      openNotification(
        error?.message || error?.errorMessage || error,
        NOTIFICATIONS.error,
      ),
  })

  const [runApplicantWorkflowMutation, runApplicantWorkflowStatus] =
    useMutation(api.runApplicantWorkflow, handlers('ran workflow'))
  const [createApplicantWorkflowMutation, createApplicantWorkflowStatus] =
    useMutation(api.createApplicantWorkflow, handlers('created workflow'))
  const [updateApplicantWorkflowMutation, updateApplicantWorkflowStatus] =
    useMutation(api.updateApplicantWorkflow, handlers('saved workflow'))
  const [deleteApplicantWorkflowMutation, deleteApplicantWorkflowStatus] =
    useMutation(api.deleteApplicantWorkflow, handlers('deleted workflow'))

  return {
    runApplicantWorkflow: runApplicantWorkflowMutation,
    runApplicantWorkflowLoading: runApplicantWorkflowStatus?.isLoading,
    createApplicantWorkflow: createApplicantWorkflowMutation,
    createApplicantWorkflowLoading: createApplicantWorkflowStatus?.isLoading,
    updateApplicantWorkflow: updateApplicantWorkflowMutation,
    updateApplicantWorkflowLoading: updateApplicantWorkflowStatus?.isLoading,
    deleteApplicantWorkflow: deleteApplicantWorkflowMutation,
    deleteApplicantWorkflowLoading: deleteApplicantWorkflowStatus?.isLoading,
  }
}

// raw SentiLink fields that put the Identity Risk card into the
// not-triggered ("Not Started") state. `sentilinkReasonCodes` must stay present
// (even if empty) so `mapSentilink` keeps the flat-manager-field path instead of
// falling back to the PDF payload shape.
const SENTILINK_RESET_NOT_TRIGGERED_FIELDS = {
  sentilinkRecordId: null,
  sentilinkTriggered: false,
  sentilinkScore: null,
  sentilinkOutcome: null,
  sentilinkRiskTier: null,
  sentilinkTransactionId: null,
  sentilinkCalledAt: null,
  sentilinkReasonCodes: [] as unknown[],
  sentilinkResult: null,
  sentilinkNoUsSsnReason: '',
}

const applyIdFraudRiskResetToApp = (app: any) => {
  const previousCount = Number(app?.sentilinkResetCount)
  // A non-zero reset count is what flips the card copy from "Not started" to
  // "Awaiting re-submission" (see `deriveSentilinkView`).
  const nextCount = Number.isFinite(previousCount) ? previousCount + 1 : 1
  return {
    ...app,
    ...SENTILINK_RESET_NOT_TRIGGERED_FIELDS,
    sentilinkResetCount: nextCount,
  }
}

// optimistically flip the reset applicant's Identity Risk card to
// "Awaiting re-submission" in every cached deal-summary payload. This is the
// authoritative UI update on success because the summary endpoint responds with
// `Cache-Control: public, max-age=20`, so an immediate refetch would be served a
// stale BLOCKED payload from the browser HTTP cache and undo the change.
const optimisticallyMarkIdFraudRiskReset = (
  applicationId: string | number | undefined,
) => {
  if (applicationId == null) {
    return
  }
  const targetId = String(applicationId)

  const summaryQueries = queryCache.getQueries(
    (query) =>
      Array.isArray(query.queryKey) &&
      (query.queryKey[0] === 'dealApplicationsSummary' ||
        query.queryKey[0] === 'fullApplications'),
  )

  summaryQueries.forEach((query) => {
    queryCache.setQueryData(query.queryKey, (current: any) => {
      if (!Array.isArray(current)) {
        return current
      }
      let didPatch = false
      const next = current.map((app: any) => {
        const appId = app?.applicationSummary?.id ?? app?.id
        if (appId == null || String(appId) !== targetId) {
          return app
        }
        didPatch = true
        return applyIdFraudRiskResetToApp(app)
      })
      return didPatch ? next : current
    })
  })
}

const getManualVerificationFromResponse = (data: any, variables?: any) => {
  const fromApi =
    data?.identityManualVerification ?? data?.identity_manual_verification ?? null
  const reviewedItem =
    fromApi?.reviewedItem ??
    fromApi?.reviewed_item ??
    variables?.reviewedItem ??
    null
  const reviewedItemLabel =
    fromApi?.reviewedItemLabel ??
    fromApi?.reviewed_item_label ??
    getSentilinkVerifyReviewedItemLabel(reviewedItem)
  const detailsFromVariables =
    typeof variables?.details === 'string' && variables.details.trim()
      ? variables.details.trim()
      : null
  const details = fromApi?.details ?? detailsFromVariables
  const verifiedAt =
    fromApi?.verifiedAt ??
    fromApi?.verified_at ??
    (data?.identityManuallyVerified === true ||
    data?.identity_manually_verified === true ||
    fromApi ||
    reviewedItem
      ? new Date().toISOString()
      : null)

  if (!fromApi && !verifiedAt && !reviewedItem) {
    return null
  }

  return {
    ...(fromApi && typeof fromApi === 'object' ? fromApi : {}),
    ...(fromApi?.reviewerName != null || fromApi?.reviewer_name != null
      ? {
          reviewerName: fromApi?.reviewerName ?? fromApi?.reviewer_name ?? null,
        }
      : {}),
    ...(fromApi?.reviewerEmail != null || fromApi?.reviewer_email != null
      ? {
          reviewerEmail:
            fromApi?.reviewerEmail ?? fromApi?.reviewer_email ?? null,
        }
      : {}),
    ...(reviewedItem != null ? { reviewedItem } : {}),
    ...(reviewedItemLabel != null ? { reviewedItemLabel } : {}),
    ...(details != null ? { details } : {}),
    ...(verifiedAt != null ? { verifiedAt } : {}),
  }
}

const applyIdFraudRiskVerifyToApp = (app: any, verification: any) => {
  const sentilinkResult =
    app?.sentilinkResult && typeof app.sentilinkResult === 'object'
      ? {
          ...app.sentilinkResult,
          identityManuallyVerified: true,
          identityManualVerification:
            verification ?? app.sentilinkResult.identityManualVerification ?? null,
          sentilinkActiveForIdentityDecisioning: false,
        }
      : app?.sentilinkResult

  return {
    ...app,
    identityManuallyVerified: true,
    identityManualVerification:
      verification ?? app?.identityManualVerification ?? null,
    sentilinkActiveForIdentityDecisioning: false,
    ...(sentilinkResult !== app?.sentilinkResult ? { sentilinkResult } : {}),
  }
}

const optimisticallyMarkIdFraudRiskVerified = (
  applicationId: string | number | undefined,
  verification: any,
) => {
  if (applicationId == null) {
    return
  }
  const targetId = String(applicationId)

  const summaryQueries = queryCache.getQueries(
    (query) =>
      Array.isArray(query.queryKey) &&
      (query.queryKey[0] === 'dealApplicationsSummary' ||
        query.queryKey[0] === 'fullApplications'),
  )

  summaryQueries.forEach((query) => {
    queryCache.setQueryData(query.queryKey, (current: any) => {
      if (!Array.isArray(current)) {
        return current
      }
      let didPatch = false
      const next = current.map((app: any) => {
        const appId = app?.applicationSummary?.id ?? app?.id
        if (appId == null || String(appId) !== targetId) {
          return app
        }
        didPatch = true
        return applyIdFraudRiskVerifyToApp(app, verification)
      })
      return didPatch ? next : current
    })
  })
}

export function useResetIdFraudRiskCheck(options: { onSuccess?: Function } = {}) {
  const [resetIdFraudRiskCheck, { isLoading }] = useMutation(
    api.resetIdFraudRiskCheck,
    {
      onSuccess: async (data, variables) => {
        // Instant, cache-proof flip of the card to "Awaiting re-submission".
        // Runs only on a confirmed 200 (all reset failures return 4xx and route
        // to `onError`), so a failed/errored reset never shows the awaiting state.
        optimisticallyMarkIdFraudRiskReset(variables?.applicationId)
        // Refresh the deal shell + per-application full view. These are either a
        // different endpoint (deal shell) or inactive during the deal-summary
        // reset (full view), so they do not clobber the optimistic summary state.
        await queryCache.invalidateQueries('dealApplicationsSummaryDeal')
        await queryCache.invalidateQueries('getFullApplication')
        openNotification(
          'Identity Risk Check was reset.',
          NOTIFICATIONS.info,
        )
        if (options.onSuccess) {
          await options.onSuccess(data, variables)
        }
      },
      onError: (error: any) => {
        const nonFieldErrors = error?.errors?.nonFieldErrors
        const backendDetail = Array.isArray(nonFieldErrors)
          ? nonFieldErrors[0]
          : undefined

        openNotification(
          backendDetail ||
            error?.response?.data?.detail ||
            error?.message ||
            error?.errorMessage ||
            'Failed to reset the Identity Risk Check.',
          NOTIFICATIONS.error,
        )
      },
    },
  )

  return {
    resetIdFraudRiskCheck,
    isResettingIdFraudRiskCheck: isLoading,
  }
}

const SENTILINK_VERIFY_ERROR_MESSAGES = {
  status_locked:
    'This application has already been approved and cannot be verified.',
  deal_decisioned:
    'This deal has already been decisioned and its Identity Risk Check cannot be verified.',
  deal_cancelled:
    'This deal has already been cancelled and its Identity Risk Check cannot be verified.',
  concurrent_update:
    'This application changed while verification was starting. Refresh and try again.',
}

export function useVerifyIdFraudRiskCheck(options: { onSuccess?: Function } = {}) {
  const [verifyIdFraudRiskCheck, { isLoading }] = useMutation(
    api.verifyIdFraudRiskCheck,
    {
      onSuccess: async (data, variables) => {
        const verification =
          getManualVerificationFromResponse(data, variables) ?? {
            verifiedAt: new Date().toISOString(),
          }
        optimisticallyMarkIdFraudRiskVerified(
          variables?.applicationId,
          verification,
        )
        const firstName =
          typeof variables?.applicantFirstName === 'string' &&
          variables.applicantFirstName.trim()
            ? variables.applicantFirstName.trim()
            : null
        openNotification(
          firstName
            ? `Identity Risk Check verified for ${firstName} · Lumen Recalculating, please refresh your screen`
            : 'Identity Risk Check verified · Lumen Recalculating, please refresh your screen',
          NOTIFICATIONS.info,
        )
        // Close the verify dialog immediately after the optimistic patch +
        // toast. Do not wait on cache invalidation refetches (deal shell /
        // full app / Lumen) — those can take seconds and leave the modal stuck.
        if (options.onSuccess) {
          await options.onSuccess(data, variables)
        }
        // Deal shell / full app only — do not refetch dealApplicationsSummary
        // (HTTP Cache-Control max-age=20 would clobber the optimistic verified state).
        // Backend verify enqueues a Lumen re-run; refresh so Outcomes / Lumen
        // tabs pick up the new run instead of keeping the pre-verify result.
        void Promise.all([
          queryCache.invalidateQueries('dealApplicationsSummaryDeal'),
          queryCache.invalidateQueries('getFullApplication'),
          queryCache.invalidateQueries('getLatestLumenV2Run'),
        ])
      },
      onError: (error: any) => {
        const nonFieldErrors = error?.errors?.nonFieldErrors
        const backendNonFieldDetail = Array.isArray(nonFieldErrors)
          ? nonFieldErrors[0]
          : undefined
        const errorCode =
          error?.errors?.code ?? error?.response?.data?.code ?? undefined
        const codedMessage =
          SENTILINK_VERIFY_ERROR_MESSAGES[
            errorCode as keyof typeof SENTILINK_VERIFY_ERROR_MESSAGES
          ]

        // Prefer ApiError fields the axios interceptor may populate from DRF
        // `{code, detail}` payloads so managers see a stable terminal-state reason.
        openNotification(
          codedMessage ||
            error?.errors?.error ||
            backendNonFieldDetail ||
            error?.errors?.detail ||
            error?.response?.data?.detail ||
            error?.message ||
            error?.errorMessage ||
            'Failed to verify the Identity Risk Check.',
          NOTIFICATIONS.error,
        )
      },
    },
  )

  return {
    verifyIdFraudRiskCheck,
    isVerifyingIdFraudRiskCheck: isLoading,
  }
}

export function useListApplicantPayments(applicant) {
  const {
    data: payments,
    isLoading,
    refetch,
  } = useQuery(
    [LIST_APPLICANT_PAYMENTS, { applicant }],
    () => api.listApplicantPayments(applicant),
    {
      enabled: !!applicant,
    },
  )

  return {
    payments,
    isLoading,
    refetch,
  }
}

export function useUpsertApplicantPayment(applicant, location, step, party) {
  const {
    data: payment,
    isLoading,
    refetch,
  } = useQuery(
    [UPSERT_APPLICANT_PAYMENT, { applicant, location, step, party }],
    () => api.upsertApplicantPayment(applicant, location, `${step}:${party}`),
    {
      enabled: applicant && location && step && party,
    },
  )

  return {
    payment,
    isLoading,
    refetch,
  }
}

export function useResolveApplicantPayments(applicant, location, step, party) {
  return _.chain([
    useListApplicantPayments(applicant)?.payments,
    useUpsertApplicantPayment(applicant, location, step, party)?.payment,
  ])
    .compact()
    .flatten()
    .uniqBy('id')
    .value()
}

export const useGetOcrJobs = (
  {
    id,
    person,
  }: { id: string; person?: never } | { id?: never; person: string },
  enable: boolean,
) => {
  const {
    data: ocrJobs,
    isLoading: isOcrJobsLoading,
    refetch,
  } = useQuery(
    [GET_OCR_JOBS, { ...(id && { id }), ...(person && { person }) }],
    () => api.getOcrJobs(id, person),
    {
      enabled: enable,
      onError: () => {
        openNotification(
          'Failed to get document processing jobs',
          NOTIFICATIONS.error,
        )
      },
    },
  )

  return {
    ocrJobs: ocrJobs?.data,
    isOcrJobsLoading,
    refetchOcrJobs: refetch,
  }
}

export const useGetDocuments = (owner: string, enable: boolean) => {
  const {
    data: documents,
    isLoading: isDocumentsLoading,
    refetch,
  } = useQuery([GET_DOCUMENTS, { owner }], () => api.getDocuments(owner), {
    enabled: enable,
    onError: () => {
      openNotification(
        'Failed to get document processing jobs',
        NOTIFICATIONS.error,
      )
    },
  })

  return {
    documents: documents?.data,
    isDocumentsLoading,
    refetchDocuments: refetch,
  }
}

export const useGetDocumentDetail = (id: string) => {
  const { data: documents, ...rest } = useQuery(
    [GET_DOCUMENT_DETAIL, id],
    () => api.getDocumentDetail(id),
    {
      onError: () => {
        openNotification(
          'Failed to get document processing jobs',
          NOTIFICATIONS.error,
        )
      },
    },
  )

  return {
    document: documents?.data,
    ...rest,
  }
}

export const useDeleteDocumentById = (applicationId: number) => {
  const applicationQueryKey = isManagerApp()
    ? [APPLICATION_QUERY_KEYS.getFullApplication(applicationId)]
    : APPLICATION_RENTER_QUERY_KEYS.getApplicationSummary(applicationId)
  const [mutation, rest] = useMutation(api.deleteDocument, {
    onMutate: (id) => {
      queryCache.cancelQueries(applicationQueryKey)
      const previousApplication = queryCache.getQueryData(applicationQueryKey)
      const updateRequiredDocument = (documents: DocumentInApplication[]) =>
        documents.map(({ documents, ...rest }) => ({
          documents: documents.filter((document) => document.id !== id),
          ...rest,
        }))
      const updateDocument = (documents: Document[]) =>
        documents.filter((document) => document.id !== id)

      queryCache.setQueryData(applicationQueryKey, (application) =>
        _.chain(application)
          .update('financialDocuments', updateRequiredDocument)
          .update('requiredDocuments', updateRequiredDocument)
          .update('unverifiedIncomeDocuments', updateRequiredDocument)
          .update('unverifiedBackgroundDocuments', updateRequiredDocument)
          .update('snapshot.applicantDocuments', updateDocument)
          .update('snapshot.originalDocuments', updateDocument)
          .value(),
      )
      return { previousApplication }
    },
    onSuccess: () => {
      openNotification(
        'Successfully removed the document from the application',
        NOTIFICATIONS.info,
      )
    },
    onError: (error, doc, context: { previousApplication: any }) => {
      queryCache.setQueryData(applicationQueryKey, context.previousApplication)
      openNotification(
        'Failed to remove the document from the application',
        NOTIFICATIONS.error,
      )
    },
  })

  return {
    deleteDocumentById: mutation,
    ...rest,
  }
}

export const useUploadDocument = (applicationId: number) => {
  const applicationQueryKey = isManagerApp()
    ? [APPLICATION_QUERY_KEYS.getFullApplication(applicationId)]
    : APPLICATION_RENTER_QUERY_KEYS.getApplicationSummary(applicationId)
  const [mutation, rest] = useMutation(
    ({
      description,
      filename,
      ocr,
      owner,
      ownerType,
      companySlug,
      propertyName,
      documentIntent,
    }: Pick<Document, 'description' | 'filename' | 'owner' | 'ownerType'> & {
      file: File
      ocr: boolean
      companySlug?: string
      propertyName?: string
      documentIntent?: string
    }) =>
      api.createDocument({
        description,
        filename,
        ocr,
        owner,
        ownerType,
        companySlug,
        propertyName,
        documentIntent,
      }),
    {
      onError: () => {
        openNotification(
          'The file upload failed, please try a different file or try again.',
          NOTIFICATIONS.error,
        )
      },
      onSuccess: async ({ data: response }, { file, ownerType }) => {
        try {
          await api.uploadDocument(response.uploadUrl, file)
        } catch (error) {
          await api.deleteDocument(response.id)
          openNotification(
            'The file upload failed, please try a different file or try again.',
            NOTIFICATIONS.error,
          )
          return false
        }
        queryCache.setQueryData(
          applicationQueryKey,
          (application: {
            financialDocuments: Document[]
            snapshot: {
              applicantDocuments: Document[]
              originalDocuments: Document[]
            }
          }) => {
            const updateRequiredDocument = (
              requiredDocuments: DocumentInApplication[],
            ) =>
              requiredDocuments.map(
                ({ documents, formType, name, ...rest }) => ({
                  documents:
                    formType === response.type || name === response.description
                      ? [...(documents || []), response]
                      : documents,
                  formType,
                  name,
                  ...rest,
                }),
              )

            return _.chain(application)
              .update('financialDocuments', updateRequiredDocument)
              .update('requiredDocuments', updateRequiredDocument)
              .update('unverifiedIncomeDocuments', updateRequiredDocument)
              .update('unverifiedBackgroundDocuments', updateRequiredDocument)
              .update(
                `snapshot.${
                  ownerType === 'person'
                    ? 'originalDocuments'
                    : 'applicantDocuments'
                }`,
                (documents: Document[]) => [...(documents || []), response],
              )
              .value()
          },
        )
      },
    },
  )
  return { uploadDocument: mutation, ...rest }
}
